import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { usePortal, findPortal, findPortalByRole, ACTIVATION_PATH } from "../contexts/PortalContext";
import { useAuth } from "../contexts/AuthContext";

/**
 * Mounted at /:slug for every portal slug (customer, student, food, hostel,
 * driver, iprint, merchant, organizer).
 *
 *   • Persists the portal in PortalContext (localStorage) — survives reload + PWA install.
 *   • If `?activate=<qr>` is present in the query, forwards to the matching
 *     paid-card registration form for that role (with the QR pre-filled).
 *   • If user is already authenticated, forwards them to /app.
 *   • Otherwise forwards them to the portal's dedicated login path.
 */
export default function PortalEntry() {
  const { pathname, search } = useLocation();
  const slug = (pathname.split("/").filter(Boolean)[0] || "").toLowerCase();
  const nav = useNavigate();
  const { set } = usePortal();
  const { user } = useAuth();
  const portal = findPortal(slug);

  useEffect(() => {
    if (!portal) { nav("/login", { replace: true }); return; }
    set(portal.slug);

    // Activation hand-off — a paid card was scanned. Forward to the
    // registration page for this role with the QR pre-filled.
    // Different registration components expect different query-param names
    // (legacy quirk), so we pass them all and let each component pick:
    //   • token=<qr>  — Activate.jsx, StudentActivate.jsx, IPrintShopActivate.jsx,
    //                   TransportDriverActivate.jsx, StaffActivate.jsx
    //   • qr=<qr>     — VendorRegister.jsx, HostelOwnerRegister.jsx
    //   • card=<qr>   — Activate.jsx alternative
    const params = new URLSearchParams(search);
    const activateQr = params.get("activate") || params.get("qr") || params.get("serial");
    if (activateQr) {
      const path = ACTIVATION_PATH[portal.slug];
      if (path) {
        const q = encodeURIComponent(activateQr);
        // `path` already contains a `?portal=…` query — append with `&` not `?`.
        const sep = path.includes("?") ? "&" : "?";
        nav(`${path}${sep}activate=${q}&token=${q}&qr=${q}&card=${q}&serial=${q}`, { replace: true });
        return;
      }
    }

    if (user) {
      // Already authed — log a portal-mismatch hint then drop into the app.
      const own = findPortalByRole(user.role);
      if (own && own.slug !== portal.slug) {
        // The role-aware dashboard router inside /app will land them on the
        // right home. No need to block here.
      }
      nav("/app", { replace: true });
    } else {
      nav(portal.loginPath, { replace: true });
    }
  }, [portal, user, set, nav, search]);

  return (
    <div className="min-h-screen bg-[#09090F] flex items-center justify-center">
      <Loader2 className="animate-spin text-[#A78BFA]" />
    </div>
  );
}
